<template>
    <NuxtLink
        ref="el"
        :to="articleUri(article.uri)"
        class="group rounded-card-md transition-background-color flex h-full gap-4 overflow-hidden duration-300"
        :class="{
            'flex-col lg:grid lg:grid-cols-[minmax(0,1.5fr)_minmax(0,2fr)] lg:gap-x-4':
                block?.Orientation === 'horizontal' && !forceVertical,
            'flex-col': block?.Orientation === 'vertical' || forceVertical,
            'border-neutral-base hover:border-neutral-subtle surface-primary hover:surface-primary border':
                block?.CardStyle !== 'no-background',
            'p-4 lg:p-6': block?.CardStyle === 'spacious',
        }"
        :data-mode="mode"
        @mouseover="isHovered = true"
        @mouseout="isHovered = false"
    >
        <!-- Image -->
        <div
            class="w-full flex-none"
            :class="{
                'h-full flex-auto': block?.Orientation === 'horizontal' && !forceVertical,
                'lg:w-full': block?.Orientation === 'vertical' || forceVertical,
            }"
        >
            <Image
                :image="article.image[0]"
                class="h-full w-full object-cover transition-transform duration-500"
                :class="{
                    'aspect-362/240': block?.CardStyle !== 'spacious',
                    'aspect-296/240': block?.CardStyle === 'spacious',
                    'rounded-card-md': block?.CardStyle === 'spacious',
                }"
            />
        </div>
        <!-- Content -->
        <div
            class="grid flex-none content-center space-y-2"
            :class="{
                'px-4 pb-4 lg:px-6 lg:pb-6': !['spacious', 'no-background'].includes(block?.CardStyle),
                'p-4 lg:p-6': block?.Orientation === 'horizontal' && !forceVertical,
            }"
        >
            <small class="flex flex-wrap items-center gap-2">
                <!-- Top Label -->
                <div
                    v-if="props.block.ShowTopLabel === true"
                    class="rounded-button-xs button-brand-primary-on-surface-default flex items-center gap-x-1.5"
                    :class="{
                        'button-brand-primary-surface-default inline-flex px-1.5 py-0.5 text-sm':
                            topLabelStyle === 'badge',
                    }"
                >
                    <span
                        :class="{
                            'button-brand-primary-surface-default rounded-card-sm p-1': topLabelStyle === 'icon',
                        }"
                    >
                        <DIcon
                            v-if="articleicon"
                            :icon="`ph:${articleicon}`"
                            size="w-5 h-5"
                        />
                    </span>
                    <p
                        v-if="topLabelStyle !== 'icon'"
                        class="text-medium"
                    >
                        {{ articlelabel }}
                    </p>
                </div>

                <div class="flex flex-nowrap gap-2 whitespace-nowrap">
                    <span
                        v-if="topLabelStyle === 'icon' && block?.ShowTopLabel === true"
                        class="text-neutral-base text-medium"
                    >
                        {{ articlelabel }}
                    </span>
                    <span
                        aria-hidden="true"
                        class="shape-neutral-base"
                        :class="{
                            hidden: block?.TopLabelStyle !== 'icon' || block?.ShowTopLabel !== true,
                        }"
                    >
                        &bull;
                    </span>
                    <small class="text-neutral-subtle font-ux text-sm">
                        <DateFormat
                            format="ddd D MMM YYYY"
                            :date="article.postDate"
                        />
                    </small>
                </div>
            </small>
            <h2
                class="text-heading font-heading font-brand-heading line-clamp-2 flex-1 text-xl underline-offset-4 transition-colors duration-150 group-hover:underline"
            >
                {{ article.title }}
            </h2>
            <p class="leading-lg line-clamp-3 flex-1 text-base text-(--text-body)">
                {{ article.articleExcerpt }}
            </p>
            <div v-if="block.ButtonText?.length > 0">
                <DButton
                    theme="outline"
                    class="mt-2 flex-0"
                >
                    {{ block.ButtonText }}
                </DButton>
            </div>
        </div>
    </NuxtLink>
</template>

<script setup>
    import { DButton, DIcon } from '@digistorm/spark'
    import { useResizeObserver } from '@vueuse/core'
    import { computed } from 'vue'
    const { articleUri } = useArticleUri()

    const el = ref(null)
    const forceVertical = ref(false)

    const props = defineProps({
        article: Object,
        block: Object,
        articlelabel: String,
        articleicon: String,
    })

    // Detects if not enough space for horizontal layout
    useResizeObserver(el, (entries) => {
        const entry = entries[0]
        const { width } = entry.contentRect
        forceVertical.value = Math.ceil(width) < 500
    })

    const isHovered = ref(false)

    const paletteMode = computed(() => {
        switch (props.block?.ColorPalette) {
            case 'color-subtle':
                return 'brand'
            case 'color-invert':
                return 'brand-invert'
            default:
                return props.block?.ColorPalette || ''
        }
    })

    const mode = computed(() => {
        if (props.block.CardStyle !== 'no-background') {
            return isHovered.value ? 'brand-invert' : paletteMode.value
        }
        return paletteMode.value
    })

    const topLabelStyle = computed(() => {
        switch (props.block?.TopLabelStyle) {
            case 'icon':
                return 'icon'
            default:
                return 'badge'
        }
    })
</script>
